import { Controller } from '@hotwired/stimulus'
import { throttle } from 'throttle-debounce'

export default class FilterViewController extends Controller<HTMLElement> {
  declare contentWrapper: HTMLElement
  declare expandedValue: boolean
  declare mediaQueryList: MediaQueryList
  declare mobileValue: boolean
  declare readonly accordionTargets: HTMLDetailsElement[]
  declare readonly sidebarFormContentTarget: HTMLElement
  declare readonly sidebarFormTarget: HTMLFormElement
  declare readonly sidebarTarget: HTMLElement
  declare readonly sidebarToggleTarget: HTMLButtonElement
  declare readonly topbarTarget: HTMLFormElement

  static targets = [
    'accordion',          // Each filter exists within an accordion component
    'topbar',             // The top bar form that displays currently applied filters
    'sidebar',            // The sidebar contains a form
    'sidebarForm',        // The sidebar form is a collection of filter accordions
    'sidebarFormContent', // The content of the sidebar form (minus the submit button)
    'sidebarToggle',      // The button that toggles the sidebar
  ]

  static values = {
    expanded: Boolean,
    mobile: Boolean,
  }

  SIDEBAR_GAP = '1.5rem'
  SIDEBAR_WIDTH = '16rem'
  SIDEBAR_OFFSET = [
    '-',
    (parseFloat(this.SIDEBAR_WIDTH) + parseFloat(this.SIDEBAR_GAP)),
    'rem'
  ].join('')

  connect() {
    this.contentWrapper = document.getElementById('content-wrapper')
    this.mediaQueryList = window.matchMedia(`(max-width: 60rem)`)

    this.mobileValue = this.mediaQueryList.matches
    this.expandedValue = this.#getDerivedExpandedValue()

    window.addEventListener('resize', this.#setSidebarContentMaxHeight)
    this.contentWrapper.addEventListener('scroll', this.#setSidebarContentMaxHeight)
    this.mediaQueryList.addEventListener('change', this.#handleMediaQueryChange.bind(this))

    // HACK: Wait for the next tick to trigger the method
    // Otherwise, the height will be calculated incorrectly
    setTimeout(() => this.#setSidebarContentMaxHeight(), 10)
  }

  disconnect() {
    window.removeEventListener('resize', this.#setSidebarContentMaxHeight)
    this.contentWrapper.removeEventListener('scroll', this.#setSidebarContentMaxHeight)
    this.mediaQueryList.removeEventListener('change', this.#handleMediaQueryChange.bind(this))
  }

  expandedValueChanged(isExpanded: boolean, wasExpanded: boolean) {
    this.#determineWhichAccordionsToOpen()
    this.sidebarToggleTarget.setAttribute('aria-expanded', String(isExpanded))
    this.element.classList.toggle('expanded', isExpanded)

    if (this.mobileValue) {
      this.contentWrapper.classList.toggle('!overflow-hidden', isExpanded)
      this.sidebarFormTarget.reset()
    }

    if (wasExpanded === undefined || this.mobileValue) return

    localStorage.setItem('filterViewSidebarOpen', String(isExpanded))
  }

  mobileValueChanged(isMobile: boolean) {
    if (isMobile) {
      this.expandedValue = false
    } else {
      this.expandedValue = this.#getDerivedExpandedValue()
    }

    this.#setSidebarContentMaxHeight()
  }

  // Public methods

  toggleSidebar() {
    if (!document.startViewTransition) {
      this.expandedValue = !this.expandedValue
    } else {
      document.startViewTransition(function() {
        this.expandedValue = !this.expandedValue
      }.bind(this))
    }
  }

  // 'Private' methods

  #determineWhichAccordionsToOpen() {
    if (this.#elemHasActiveFilters(this.sidebarTarget) && !this.mobileValue) {
      this.#openPopulatedAccordions()
    } else {
      this.#openAllAccordions()
    }
  }

  #elemHasActiveFilters(element: HTMLElement) {
    const hasCheckedBoxes = Boolean(element.querySelector('input[type=checkbox]:checked'))
    const hasFilledSearches = Boolean(element.querySelector('input[type=search][value]'))

    return Boolean(hasCheckedBoxes || hasFilledSearches)
  }

  #getDerivedExpandedValue() {
    const storedValue = localStorage.getItem('filterViewSidebarOpen')

    if (this.mobileValue) return false
    else if (storedValue === null) return true
    else return storedValue === 'true'
  }

  #handleMediaQueryChange(event: MediaQueryList) {
    this.mobileValue = event.matches
  }

  #openAllAccordions() {
    this.accordionTargets.forEach((accordion) => accordion.open = true)
  }

  #openPopulatedAccordions() {
    this.accordionTargets.forEach((accordion) => {
      if (this.#elemHasActiveFilters(accordion)) accordion.open = true
    })
  }

  #setSidebarContentMaxHeight = throttle(20, function() {
    if (this.mobileValue || !this.expandedValue) {
      delete this.sidebarFormContentTarget.style.maxHeight
      return
    }

    const contentWrapper = this.contentWrapper || document.getElementById('content-wrapper')
    const gutterHeight = 16 // Space between content and form's submit button, in pixels
    const viewportHeight = window.innerHeight
    const formHeight = this.sidebarFormTarget.getBoundingClientRect().height
    const formContentHeight = this.sidebarFormContentTarget.getBoundingClientRect().height
    const formContentTop = this.sidebarFormContentTarget.getBoundingClientRect().top

    // Get the height of the area that's beneath the form content
    const formSubmitHeight = formHeight - formContentHeight

    // Get the distance from the top of the content
    // wrapper to the top of the form content
    const formContentTopFromContentWrapper =
      formContentTop
        - contentWrapper.getBoundingClientRect().top
        - gutterHeight

    if (formContentTopFromContentWrapper < 1) return

    // Calculate the maximum height the form content can be
    const maxHeight = Math.floor(
      viewportHeight
        - formContentTop
        - formSubmitHeight
        - gutterHeight
    )

    // Get the form content's current max height value
    const currentMaxHeight = parseInt(
      this.sidebarFormContentTarget.style.maxHeight,
      10
    )

    if (maxHeight === currentMaxHeight) return

    this.sidebarFormContentTarget.style.maxHeight = `${maxHeight}px`
  }.bind(this))
}
